import { stripForbiddenControlCharacters } from '../utils/controlCharacters';

// Константы для вакансий
export const MAIN_CONTENT_WIDTH = 1100;

/** Внутренний лимит названия вакансии в Relevanter. */
export const VACANCY_TITLE_MAX_LENGTH = 500;

/** Ограничение HeadHunter применяется только при публикации/обновлении вакансии в HH. */
export const HH_VACANCY_TITLE_MAX_LENGTH = 100;
export const HH_KEY_SKILLS_MAX_COUNT = 30;
export const HH_KEY_SKILL_MAX_LENGTH = 100;
export const VACANCY_DESCRIPTION_MAX_LENGTH = 10000;
export const VACANCY_INTERNAL_TAG_MAX_LENGTH = 500;
export const VACANCY_OFFICE_ADDRESS_MAX_LENGTH = 1000;
export const VACANCY_TOPIC_MAX_LENGTH = 500;
export const VACANCY_SKILL_GROUPS_MAX_LENGTH = 50000;

const HH_TEXTUAL_CONTROL_ESCAPE_PATTERN = /\\x([0-9a-fA-F]{2})/g;
const HH_FORBIDDEN_CONTROL_CHARACTERS_PATTERN = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g;

/** Повторяет plain-text нормализацию Relevanter перед отправкой данных в HeadHunter. */
export function normalizeHhVacancyTitle(value: string): string {
  return value
    .replace(HH_TEXTUAL_CONTROL_ESCAPE_PATTERN, (match, hex: string) => {
      const code = Number.parseInt(hex, 16);
      return (code >= 0x00 && code <= 0x1f) || (code >= 0x7f && code <= 0x9f)
        ? ''
        : match;
    })
    .replace(HH_FORBIDDEN_CONTROL_CHARACTERS_PATTERN, '')
    .trim();
}

export function getHhVacancyTitleLength(title: string): number {
  return normalizeHhVacancyTitle(title).length;
}

export function normalizeInternalVacancyTitle(title: string): string {
  return stripForbiddenControlCharacters(title, 'position.title');
}

export function getInternalVacancyTitleLength(title: string): number {
  return normalizeInternalVacancyTitle(title).length;
}

export function validateInternalVacancyTitle(title: string): string | undefined {
  if (getInternalVacancyTitleLength(title) > VACANCY_TITLE_MAX_LENGTH) {
    return `Название вакансии не должно превышать ${VACANCY_TITLE_MAX_LENGTH} символов`;
  }
  return undefined;
}

export function validateHhVacancyTitle(title: string): string | undefined {
  const normalizedTitle = normalizeHhVacancyTitle(title);
  if (!normalizedTitle) {
    return 'Название вакансии обязательно';
  }
  if (normalizedTitle.length > HH_VACANCY_TITLE_MAX_LENGTH) {
    return `Название не должно превышать ${HH_VACANCY_TITLE_MAX_LENGTH} символов для HH.ru (сейчас: ${normalizedTitle.length})`;
  }
  return undefined;
}

type HhVacancyExportLimits = {
  title: string;
  descriptionPlainLength: number;
  descriptionHtmlLength: number;
  descriptionMinLength: number;
  descriptionMaxLength: number;
  keySkills: string[];
};

export function validateHhVacancyExport({
  title,
  descriptionPlainLength,
  descriptionHtmlLength,
  descriptionMinLength,
  descriptionMaxLength,
  keySkills,
}: HhVacancyExportLimits): string[] {
  const errors: string[] = [];
  const titleError = validateHhVacancyTitle(title);
  if (titleError) errors.push(titleError);

  if (descriptionPlainLength === 0) {
    errors.push('Описание вакансии обязательно для HH.ru');
  } else if (descriptionPlainLength < descriptionMinLength) {
    errors.push(
      `Описание должно содержать не менее ${descriptionMinLength} символов для HH.ru (сейчас: ${descriptionPlainLength})`,
    );
  }
  if (descriptionHtmlLength > descriptionMaxLength) {
    errors.push(
      `Итоговое HTML-описание не должно превышать ${descriptionMaxLength} символов для HH.ru (сейчас: ${descriptionHtmlLength})`,
    );
  }

  const normalizedKeySkills = keySkills.map(normalizeHhVacancyTitle).filter(Boolean);
  if (normalizedKeySkills.length > HH_KEY_SKILLS_MAX_COUNT) {
    errors.push(
      `Для HH.ru можно указать не более ${HH_KEY_SKILLS_MAX_COUNT} ключевых навыков (сейчас: ${normalizedKeySkills.length})`,
    );
  }
  const oversizedSkill = normalizedKeySkills.find(
    skill => skill.length > HH_KEY_SKILL_MAX_LENGTH,
  );
  if (oversizedSkill) {
    errors.push(
      `Ключевой навык для HH.ru не должен превышать ${HH_KEY_SKILL_MAX_LENGTH} символов (сейчас: ${oversizedSkill.length})`,
    );
  }

  return errors;
}

type InternalVacancyStringLimits = {
  description?: string;
  companyDescription?: string;
  socialPackageText?: string;
  internalTag?: string;
  topics?: string[];
  skillGroups?: string;
  officeLocations?: Array<{ address?: string }>;
};

/** Проверяет те же нормализованные строки и лимиты, которые принимает business-back. */
export function validateInternalVacancyStringLimits({
  description,
  companyDescription,
  socialPackageText,
  internalTag,
  topics = [],
  skillGroups,
  officeLocations = [],
}: InternalVacancyStringLimits): string[] {
  const errors: string[] = [];
  const check = (label: string, value: string | undefined, max: number, path: string) => {
    if (value == null) return;
    const length = stripForbiddenControlCharacters(value, path).length;
    if (length > max) {
      errors.push(`${label} не должно превышать ${max} символов (сейчас: ${length})`);
    }
  };

  check('Описание вакансии', description, VACANCY_DESCRIPTION_MAX_LENGTH, 'position.description');
  check('Описание компании', companyDescription, VACANCY_DESCRIPTION_MAX_LENGTH, 'position.companyDescription');
  check('Социальный пакет', socialPackageText, VACANCY_DESCRIPTION_MAX_LENGTH, 'position.socialPackageText');
  check('Внутренняя метка', internalTag, VACANCY_INTERNAL_TAG_MAX_LENGTH, 'position.internalTag');
  check('Группы навыков', skillGroups, VACANCY_SKILL_GROUPS_MAX_LENGTH, 'position.skillGroups');
  topics.forEach((topic, index) => {
    check(`Тема №${index + 1}`, topic, VACANCY_TOPIC_MAX_LENGTH, `position.topics[${index}]`);
  });
  officeLocations.forEach((location, index) => {
    check(
      `Адрес офиса №${index + 1}`,
      location.address,
      VACANCY_OFFICE_ADDRESS_MAX_LENGTH,
      `position.officeLocations[${index}].address`,
    );
  });

  return errors;
}

// Заглушка для SVG путей (будет заменена на реальные иконки)
export const svgPaths = {
  p3f40a580: "M26 5.77806L26 67.1062",
};

// Маппинг уровней между UI и API
export const levelMapReverse: { [key: string]: string } = {
  'Junior': 'JUNIOR',
  'Middle': 'MIDDLE',
  'Senior': 'SENIOR', 
  'Lead': 'LEAD'
};

export const levelMap: { [key: string]: string } = {
  'JUNIOR': 'Junior',
  'MIDDLE': 'Middle', 
  'SENIOR': 'Senior',
  'LEAD': 'Lead',
  // Также поддерживаем значения в нижнем регистре от бэкенда
  'junior': 'Junior',
  'middle': 'Middle',
  'senior': 'Senior',
  'lead': 'Lead'
};

// Fallback значения
export const UI_LEVEL_FALLBACK = 'Middle';
export const API_LEVEL_FALLBACK = 'MIDDLE';

// Опции для селектов
export const timeOptions = [
  "1 мин",
  "1 мин 30 сек",
  "2 мин",
  "2 мин 30 сек",
  "3 мин",
  "3 мин 30 сек",
  "4 мин",
  "5 мин",
];

export const levelOptions = ["Junior", "Middle", "Senior", "Lead"];

export const questionTypeOptions = [
  {
    id: "hard-skills" as const,
    label: "В основном хард-скиллы",
  },
  {
    id: "soft-skills" as const,
    label: "В основном софт-скиллы",
  },
  { 
    id: "mixed" as const, 
    label: "Смешанный тип" 
  },
];

// Стили для компонентов
export const VACANCY_STYLES = {
  // Основные цвета (используются CSS переменные)
  primary: 'var(--brand-primary)',
  primaryHover: 'var(--brand-primary-hover)',
  secondary: 'var(--text-secondary)',
  background: 'var(--bg-primary)',
  white: 'var(--white)',
  border: 'var(--border-color)',
  text: 'var(--text-primary)',
  textSecondary: 'var(--text-secondary)',
  
  // Размеры
  borderRadius: '32px',
  borderRadiusSmall: '24px',
  borderRadiusLarge: '44px',
  
  // Отступы
  padding: '24px',
  paddingLarge: '32px',
  paddingSmall: '16px',
  
  // Высота элементов
  inputHeight: '52px',
  buttonHeight: '52px',
  
  // Шрифты
  fontFamily: "'Inter:Medium', sans-serif",
  fontFamilyBold: "'Inter:Semi_Bold', sans-serif",
  fontFamilyDisplay: "'Inter_Display:Medium', sans-serif",
  
  // Размеры шрифтов
  fontSizeSmall: '14px',
  fontSizeMedium: '16px',
  fontSizeLarge: '32px',
  
  // Высота строк
  lineHeightSmall: '20px',
  lineHeightMedium: '24px',
  lineHeightLarge: '40px',
};

// Тексты и лейблы
export const VACANCY_LABELS = {
  // Заголовки
  createTitle: 'Создание вакансии',
  editTitle: 'Редактирование вакансии',
  
  // Кнопки
  create: 'Создать вакансию',
  save: 'Сохранить изменения',
  cancel: 'Отмена',
  back: 'Назад',
  generate: 'Сгенерировать',
  saving: 'Идёт сохранение…',
  
  // Поля формы
  jobTitle: 'Название вакансии',
  aiDescription: 'Описание для AI',
  topic: 'Тема',
  tags: 'Теги',
  minScore: 'Минимальная оценка',
  timePerAnswer: 'Время на один ответ',
  level: 'Уровень',
  questionCount: 'Количество вопросов',
  questionType: 'Тип вопросов',
  
  // Сообщения
  loading: 'Загрузка данных вакансии...',
  errorLoading: 'Не удалось загрузить данные вакансии',
  errorCreating: 'Ошибка при создании вакансии',
  errorUpdating: 'Ошибка при обновлении вакансии',
  errorGenerating: 'Ошибка при генерации вакансии',
  successCreated: 'Вакансия успешно создана!',
  successUpdated: 'Вакансия успешно обновлена!',
  successGenerated: 'Вакансия успешно сгенерирована!',
  enterDescription: 'Введите описание вакансии для генерации',
  enterTitle: 'Введите название вакансии',
  
  // Условия
  conditions: 'Условия',
  minimumScore: 'Минимальная оценка',
  timePerAnswerLabel: 'Время на один ответ',
  levelLabel: 'Уровень',
};
